import { memo } from "react";
import type { ToolUIPart } from "../../shared/types";
import { InlineToolPanel } from "../InlineToolPanel";
import { baseToolName } from "./shared/utils";
import { EmailListCard } from "./EmailListCard";
import { EmailThreadCard } from "./EmailThreadCard";
import { EmailDraftCard } from "./EmailDraftCard";
import { EmailSendCard } from "./EmailSendCard";
import { CalendarListCard } from "./CalendarListCard";
import { EventConfirmCard } from "./EventConfirmCard";
import { LabelActionCard } from "./LabelActionCard";
import { SheetsListCard } from "./SheetsListCard";
import { SheetViewCard } from "./SheetViewCard";
import { SheetActionCard } from "./SheetActionCard";
import { DocsListCard } from "./DocsListCard";
import { DocViewCard } from "./DocViewCard";
import { DocActionCard } from "./DocActionCard";

interface ToolCardDispatchProps {
  part: ToolUIPart;
  onSendMessage: (text: string) => void;
  /** Email Composer v2 — see MessageListProps.sessionId. Only EmailDraftCard
   *  consumes this today (its send/save/hydrate REST calls need it). */
  sessionId?: string | null;
}

export const ToolCardDispatch = memo(function ToolCardDispatch({ part, onSendMessage, sessionId }: ToolCardDispatchProps) {
  switch (baseToolName(part.toolName)) {
    case "gmail_list_threads":
      return <EmailListCard part={part} onSendMessage={onSendMessage} />;
    case "gmail_read_thread":
      return <EmailThreadCard part={part} />;
    case "gmail_create_draft":
      // onSendMessage is still passed for the "Polish" action (asks the model
      // to revise the draft in chat) — the actual Send button no longer uses
      // it (Eli, Composer v2 Slice 1): sending goes through the REST route
      // below, never a chat message.
      return <EmailDraftCard part={part} onSendMessage={onSendMessage} sessionId={sessionId} />;
    case "gmail_send":
      return <EmailSendCard part={part} />;
    case "calendar_list_events":
      return <CalendarListCard part={part} />;
    case "calendar_create_event":
    case "calendar_update_event":
      return <EventConfirmCard part={part} onSendMessage={onSendMessage} />;
    case "gmail_modify_labels":
      return <LabelActionCard part={part} />;
    // Sheets
    case "sheets_list":
      return <SheetsListCard part={part} />;
    case "sheets_read":
      return <SheetViewCard part={part} />;
    case "sheets_write":
    case "sheets_append":
    case "sheets_create":
    case "sheets_add_tab":
      return <SheetActionCard part={part} />;
    // Docs
    case "docs_list":
      return <DocsListCard part={part} />;
    case "docs_read":
      return <DocViewCard part={part} />;
    case "docs_create":
    case "docs_append":
    case "docs_replace":
      return <DocActionCard part={part} />;
    default:
      return <InlineToolPanel part={part} />;
  }
});
